"use client";

import { buildConsentModeState, syncGoogleAnalyticsCollection, type ConsentModeInput } from "@/lib/analytics/trackingConsent";
import { getCookieConsent, hasStoredCookieConsent } from "@/lib/consent";

export const GA_CONSENT_STORAGE_KEY = "sa-cookie-consent";
const GA_CONSENT_UPDATED_EVENT = "sa:ga-consent-updated";

export type AnalyticsConsentState = "granted" | "denied";

export type StoredGaConsent = {
  analytics: AnalyticsConsentState;
  marketing: AnalyticsConsentState;
  version?: string;
  updatedAt?: string;
};

function canUseBrowser() {
  return typeof window !== "undefined";
}

function canUseGtag() {
  return canUseBrowser() && typeof window.gtag === "function";
}

function parseConsentValue(value: unknown): AnalyticsConsentState | null {
  return value === "granted" || value === "denied" ? value : null;
}

export function getStoredConsent(): StoredGaConsent | null {
  if (!canUseBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(GA_CONSENT_STORAGE_KEY);
    const parsed = (raw ? JSON.parse(raw) : {}) as Partial<StoredGaConsent>;
    if (hasStoredCookieConsent()) {
      const choice = getCookieConsent();
      const analytics = choice.analytics ? "granted" : "denied";
      const marketing = choice.marketing ? "granted" : "denied";
      const matchesDecision = parsed.analytics === analytics && parsed.marketing === marketing;
      return {
        analytics, marketing,
        version: matchesDecision && typeof parsed.version === "string" ? parsed.version : undefined,
        updatedAt: matchesDecision && typeof parsed.updatedAt === "string" ? parsed.updatedAt : undefined,
      };
    }
    const analytics = parseConsentValue(parsed.analytics);
    const marketing = parseConsentValue(parsed.marketing);
    if (!analytics || !marketing) return null;

    return {
      analytics,
      marketing,
      version: typeof parsed.version === "string" ? parsed.version : undefined,
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : undefined,
    };
  } catch {
    if (!hasStoredCookieConsent()) return null;
    const choice = getCookieConsent();
    return { analytics: choice.analytics ? "granted" : "denied", marketing: choice.marketing ? "granted" : "denied" };
  }
}

export function setConsentDefaultV2() {
  syncGoogleAnalyticsCollection(false);
  if (!canUseGtag()) return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  gtag("consent", "default", {
    ...buildConsentModeState({ analytics: "denied", marketing: "denied" }),
    wait_for_update: 500,
  });
}

function effectiveConsent(consent: ConsentModeInput): ConsentModeInput {
  if (!hasStoredCookieConsent()) return consent;
  const choice = getCookieConsent();
  // A stale UI event can deny, but cannot reopen a category the canonical
  // decision (including a failed-write denial) currently excludes.
  return {
    analytics: choice.analytics && consent.analytics === "granted" ? "granted" : "denied",
    marketing: choice.marketing && consent.marketing === "granted" ? "granted" : "denied",
  };
}

export function queueConsentModeUpdate(consent: ConsentModeInput) {
  if (!canUseBrowser()) return false;
  if (typeof window.gtag !== "function") {
    // This local queue sends no requests. It must exist before decision listeners
    // emit events, even while the external container is still consent-gated.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // Google's command protocol expects Arguments objects, not plain arrays.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    setConsentDefaultV2();
  }
  const effective = effectiveConsent(consent);
  // The default above closes collection; apply the effective decision after it,
  // immediately before Google's update and before synchronous subscribers.
  syncGoogleAnalyticsCollection(effective.analytics === "granted");
  window.gtag("consent", "update", buildConsentModeState(effective));
  return true;
}

export function updateConsentFromState(
  consent: { analytics: AnalyticsConsentState; marketing: AnalyticsConsentState },
  options: { source?: "decision" | "sync" } = {},
) {
  // A subscriber may emit an event immediately, so update Google's consent
  // queue before notifying browser listeners about the same decision.
  const effective = effectiveConsent(consent);
  queueConsentModeUpdate(effective);
  if (canUseBrowser()) {
    // The browser decision is authoritative even when Google is blocked or disabled.
    window.dispatchEvent(new CustomEvent(GA_CONSENT_UPDATED_EVENT, {
      detail: { ...effective, source: options.source ?? "sync" },
    }));
  }
}

export function subscribeGaConsentUpdates(onUpdate: (source: "decision" | "sync") => void) {
  if (!canUseBrowser()) return () => {};
  const handler = (event: Event) => onUpdate(
    (event as CustomEvent<{ source?: string }>).detail?.source === "decision" ? "decision" : "sync",
  );
  window.addEventListener(GA_CONSENT_UPDATED_EVENT, handler);
  return () => window.removeEventListener(GA_CONSENT_UPDATED_EVENT, handler);
}
